import type {
  U3202ActivatedModelRow,
  U3202AiModelChild,
  U3202AiModelInput,
  U3202ParameterSchemaDefinition,
} from '../U3202Service';
import type { U3202ModelCapabilities } from './U3202CapabilitiesTypes';
import { U3202_EMPTY_CAPABILITIES } from './U3202CapabilitiesTypes';
import { u3202ExtractParameterDefaults } from './u3202ParameterFormUtils';

/** Schéma de paramètres par défaut (température + tokens max) pour un nouveau modèle. */
export function u3202BuildDefaultParameterSchema(): U3202ParameterSchemaDefinition {
  return {
    temperature: { type: 'number', min: 0, max: 2, default: 0.7 },
    max_tokens: { type: 'integer', min: 1, max: 128000, default: 4096 },
  };
}

export interface U3202CatalogModelFormData {
  modelId: string;
  providerId: string;
  code: string;
  name: string;
  capabilities: U3202ModelCapabilities;
  active: boolean;
  modelVersion: number;
  parametersJson: string;
  parameterSchema: U3202ParameterSchemaDefinition;
  parameterValues: Record<string, unknown>;
}

/** Formate un objet paramètres en JSON lisible pour l'éditeur. */
export function u3202FormatParametersJson(parameters: Record<string, unknown>): string {
  if (!parameters || Object.keys(parameters).length === 0) {
    return '{}';
  }
  try {
    return JSON.stringify(parameters, null, 2);
  } catch {
    return '{}';
  }
}

/** Parse le JSON saisi ; lève une erreur si invalide. */
export function u3202ParseParametersJson(json: string): Record<string, unknown> {
  const trimmed = json.trim();
  if (!trimmed) {
    return {};
  }
  const parsed: unknown = JSON.parse(trimmed);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('JSON invalide');
  }
  return parsed as Record<string, unknown>;
}

/** Valide le JSON des paramètres ; retourne un code d'erreur i18n ou undefined. */
export function u3202ValidateParametersJson(json: string): string | undefined {
  try {
    u3202ParseParametersJson(json);
    return undefined;
  } catch {
    return 'errParametersJsonInvalid';
  }
}

export function createEmptyU3202CatalogModelFormData(providerId: string): U3202CatalogModelFormData {
  const defaults = { temperature: 0.7, max_tokens: 4096 };
  return {
    modelId: '',
    providerId,
    code: '',
    name: '',
    capabilities: { ...U3202_EMPTY_CAPABILITIES },
    active: true,
    modelVersion: 0,
    parametersJson: u3202FormatParametersJson(defaults),
    parameterSchema: u3202BuildDefaultParameterSchema(),
    parameterValues: { ...defaults },
  };
}

export function u3202GridRowToCatalogModelForm(
  row: U3202ActivatedModelRow,
  providerId: string,
): U3202CatalogModelFormData {
  const defaults = row.defaultParameters ?? {};
  return {
    modelId: row.modelId,
    providerId,
    code: row.modelCode,
    name: row.modelName,
    capabilities: row.capabilities ?? U3202_EMPTY_CAPABILITIES,
    active: row.active,
    modelVersion: row.modelVersion,
    parametersJson: u3202FormatParametersJson(defaults),
    parameterSchema: row.parameterSchema ?? u3202BuildDefaultParameterSchema(),
    parameterValues: { ...row.parameterValues },
  };
}

export function u3202CatalogModelFormToActivatedRow(
  form: U3202CatalogModelFormData,
  previous?: U3202ActivatedModelRow | null,
): U3202ActivatedModelRow {
  const defaultParameters = u3202ParseParametersJson(form.parametersJson);
  return {
    modelId: form.modelId,
    modelCode: form.code.trim().toLowerCase(),
    modelName: form.name.trim(),
    active: form.active,
    capabilities: form.capabilities ?? U3202_EMPTY_CAPABILITIES,
    catalogActive: form.active,
    modelVersion: form.modelVersion,
    parameterSchema: form.parameterSchema,
    defaultParameters,
    parameterValues: { ...defaultParameters },
    initialParameterDefaults: previous
      ? { ...previous.initialParameterDefaults }
      : u3202ExtractParameterDefaults(form.parameterSchema, defaultParameters),
    rowState: previous?.rowState,
  };
}

export function u3202CatalogModelDetailToForm(model: U3202AiModelChild, providerId: string): U3202CatalogModelFormData {
  const defaults = model.defaultParameters ?? {};
  return {
    modelId: model.id,
    providerId,
    code: model.code,
    name: model.name,
    capabilities: model.capabilities ?? U3202_EMPTY_CAPABILITIES,
    active: model.active,
    modelVersion: model.version,
    parametersJson: u3202FormatParametersJson(defaults),
    parameterSchema: model.parameterSchema ?? u3202BuildDefaultParameterSchema(),
    parameterValues: { ...defaults },
  };
}

export function u3202ValidateCatalogModelForm(data: U3202CatalogModelFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.code.trim()) {
    errors.code = 'errModelCodeRequired';
  }
  if (!data.name.trim()) {
    errors.name = 'errModelNameRequired';
  }
  const jsonError = u3202ValidateParametersJson(data.parametersJson);
  if (jsonError) {
    errors.parametersJson = jsonError;
  }
  return errors;
}

/**
 * Mappe une ligne de grille vers l'entrée `models[]` (maître/détail fournisseur).
 */
export function u3202ActivatedRowToModelInput(row: U3202ActivatedModelRow): U3202AiModelInput {
  return {
    id: row.modelId,
    code: row.modelCode.trim().toLowerCase(),
    name: row.modelName.trim(),
    parameterSchema: row.parameterSchema ?? u3202BuildDefaultParameterSchema(),
    defaultParameters: row.defaultParameters,
    capabilities: row.capabilities ?? U3202_EMPTY_CAPABILITIES,
    active: row.active,
  };
}
